import { type ValidatedExamEventsQuery } from '@exam-center/graphql';
import { createContext, type PropsWithChildren, useMemo, useState } from 'react';

import { type RecurringExamEventCreationFormType } from '../recurring-exam-event-creation-form-provider';

export type ValidatedExamEventWithClientId =
  ValidatedExamEventsQuery['ecValidateExamEventsCreation']['validatedExamEvents'][number] & {
    requestId: string;
  };

export interface RecurringExamEventCreationContextValue {
  examEvent: RecurringExamEventCreationFormType | null;
  setExamEvent: (event: RecurringExamEventCreationFormType) => void;
  validateExamEventsData: ValidatedExamEventWithClientId[];
  setValidateExamEventsData: (data: ValidatedExamEventWithClientId[]) => void;
  building?: {
    buildingName: string;
    roomNumber: string;
    floor: string;
    roomNameTh: string;
    seat: number;
  }[];
  setBuilding: (
    building:
      | {
          buildingName: string;
          roomNumber: string;
          floor: string;
          roomNameTh: string;
          seat: number;
        }[]
      | undefined,
  ) => void;
}

const RecurringExamEventCreationContext = createContext<
  RecurringExamEventCreationContextValue | undefined
>(undefined);

export default function RecurringExamEventCreationProvider(props: Readonly<PropsWithChildren>) {
  const { children } = props;
  const [examEvent, setExamEvent] = useState<RecurringExamEventCreationFormType | null>(null);
  const [building, setBuilding] = useState<RecurringExamEventCreationContextValue['building']>();

  const [validateExamEventsData, setValidateExamEventsData] = useState<
    ValidatedExamEventWithClientId[]
  >([]);

  const value: RecurringExamEventCreationContextValue = useMemo(() => {
    return {
      examEvent,
      setExamEvent,
      validateExamEventsData,
      setValidateExamEventsData,
      building,
      setBuilding,
    };
  }, [examEvent, validateExamEventsData, building]);

  return (
    <RecurringExamEventCreationContext.Provider value={value}>
      {children}
    </RecurringExamEventCreationContext.Provider>
  );
}

export { RecurringExamEventCreationContext };
